import React, { FC, useState } from 'react'
import cx from 'classnames'
import { Stack } from 'components/Stack'
import { Flex } from 'components/Flex'
import { Text } from 'components/Text'
import { useTranslation } from 'react-i18next'
import { Heading } from 'components/Heading'
import { Card } from 'components/Card'
import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { useFetchOrganization } from 'services/organization'
import styles from './Subscription.module.scss'
import { Spinner } from 'components/Spinner'
import { ContactDialog } from './components/ContactDialog'

export const Subscription: FC = () => {
  const { t } = useTranslation()
  const { organization, status } = useFetchOrganization()
  const [action, setAction] = useState<string | undefined>()

  return (
    <div className={styles.subscription}>
      <Stack space="xlarge">
        <Card>
          <Stack space="xlarge">
            <div>
              <Heading>{t('subscription.title')}</Heading>
              <Text>{t('subscription.description')}</Text>
            </div>
            {status === 'loading' && (
              <Flex justify="center" align="center">
                <Spinner />
              </Flex>
            )}
            {status === 'success' && organization && (
              <div className={styles.cardGrid}>
                <Flex
                  direction="vertical"
                  className={cx(
                    styles.subscriptionCard,
                    styles.basic,
                    organization?.subscription.tier === 'basic' ? styles.active : '',
                  )}
                >
                  <Stack space="small">
                    <Flex justify="between">
                      <Heading size="secondary" className={styles.subscriptionHeading}>
                        {t('subscription.basic.title')}
                      </Heading>
                      {organization?.subscription.tier === 'basic' ? (
                        <Flex align="center" className={styles.currentSubscription} gap="sm">
                          <Text component="span" weight="strong" inheritColor>
                            {t('subscription.current_plan')}
                          </Text>
                          <Flex
                            justify="center"
                            align="center"
                            inline
                            className={styles.iconContainer}
                          >
                            <Icon name="check-line" />
                          </Flex>
                        </Flex>
                      ) : (
                        <Button variant="secondary" onClick={() => setAction('basic.downgrade')}>
                          {t('subscription.downgrade', { plan: t('subscription.plans.basic') })}
                        </Button>
                      )}
                    </Flex>
                    <Stack space="xsmall" className={styles.includes}>
                      <Text inheritColor>{t('subscription.includes')}</Text>
                      <ul>
                        <Text component="li" weight="strong" inheritColor>
                          {t('subscription.basic.contents.map_navigation')}
                        </Text>
                        <Text component="li" weight="strong" inheritColor>
                          {t('subscription.basic.contents.core_layers')}
                        </Text>
                        <Text component="li" weight="strong" inheritColor>
                          {t('subscription.basic.contents.seats')}
                        </Text>
                      </ul>
                    </Stack>
                  </Stack>

                  <div className={styles.pricing}>
                    <Text weight="strong" className={styles.basicFreeClaim}>
                      {t('subscription.basic.free')}
                    </Text>
                  </div>
                </Flex>

                <Flex
                  direction="vertical"
                  className={cx(
                    styles.subscriptionCard,
                    styles.business,
                    organization?.subscription.tier === 'business' ? styles.active : '',
                  )}
                >
                  <Stack>
                    <Stack space="small">
                      <Flex justify="between">
                        <Heading size="secondary" className={styles.subscriptionHeading}>
                          {t('subscription.business.title')}
                        </Heading>
                        {organization?.subscription.tier === 'business' && (
                          <Flex align="center" className={styles.currentSubscription} gap="sm">
                            <Text component="span" weight="strong" inheritColor>
                              {t('subscription.current_plan')}
                            </Text>
                            <Flex
                              justify="center"
                              align="center"
                              inline
                              className={styles.iconContainer}
                            >
                              <Icon name="check-line" />
                            </Flex>
                          </Flex>
                        )}
                        {organization?.subscription.tier === 'basic' && (
                          <Button variant="accent" onClick={() => setAction('business.upgrade')}>
                            {t('subscription.upgrade', { plan: t('subscription.plans.business') })}
                          </Button>
                        )}
                        {organization?.subscription.tier === 'enterprise' && (
                          <Button
                            variant="secondary"
                            onClick={() => setAction('business.downgrade')}
                          >
                            {t('subscription.downgrade', {
                              plan: t('subscription.plans.business'),
                            })}
                          </Button>
                        )}
                      </Flex>
                      <Stack space="xsmall" className={styles.includes}>
                        <Text inheritColor>{t('subscription.includes')}</Text>
                        <ul className={styles.includes}>
                          <Text component="li" weight="strong" inheritColor>
                            {t('subscription.business.contents.map_navigation')}
                          </Text>
                          <Text component="li" weight="strong" inheritColor>
                            {t('subscription.business.contents.core_layers')}
                          </Text>
                          <Text component="li" weight="strong" inheritColor>
                            {t('subscription.business.contents.predictive_models')}
                          </Text>
                          <Text component="li" weight="strong" inheritColor>
                            {t('subscription.business.contents.custom_layers')}
                          </Text>
                          <Text component="li" weight="strong" inheritColor>
                            {t('subscription.business.contents.seats')}
                          </Text>
                        </ul>
                      </Stack>
                    </Stack>
                    <div>
                      <Text className={styles.lightText}>
                        {t('subscription.business.yearly_subscription')}
                      </Text>
                      <Text>
                        <Text component="span" weight="strong" className={styles.highlight}>
                          $1000
                        </Text>
                        <Text component="span" className={styles.lightText}>
                          / {t('subscription.payment_frecuency')}
                        </Text>
                      </Text>
                    </div>
                  </Stack>
                </Flex>

                <Flex
                  justify="between"
                  className={cx(
                    styles.subscriptionCard,
                    styles.enterprise,
                    organization?.subscription.tier === 'enterprise' ? styles.active : '',
                  )}
                >
                  <Stack space="small" className={styles.content}>
                    <Flex gap="md">
                      <Heading size="secondary" className={styles.subscriptionHeading}>
                        {t('subscription.enterprise.title')}
                      </Heading>
                      {organization?.subscription.tier === 'enterprise' && (
                        <Flex align="center" className={styles.currentSubscription} gap="sm">
                          <Text component="span" weight="strong" inheritColor>
                            {t('subscription.current_plan')}
                          </Text>
                          <Flex
                            justify="center"
                            align="center"
                            inline
                            className={styles.iconContainer}
                          >
                            <Icon name="check-line" />
                          </Flex>
                        </Flex>
                      )}
                    </Flex>

                    <Text>{t('subscription.enterprise.description')}</Text>
                    {organization?.subscription.tier !== 'enterprise' && (
                      <Button onClick={() => setAction('enterprise.upgrade')}>
                        {t('subscription.enterprise.contact')}
                      </Button>
                    )}
                  </Stack>
                </Flex>
              </div>
            )}
          </Stack>
        </Card>

        <ContactDialog
          key={action}
          isOpen={!!action}
          action={action}
          closeDialog={() => setAction(undefined)}
        />
      </Stack>
    </div>
  )
}

Subscription.displayName = 'Subscription'
